import React, { useCallback, useEffect, useRef, useState } from "react";
import "../styles/ConnectionsSection.css";
import { apiFetch } from "../utils/api";
import { useNotifications } from "./NotificationProvider";

const ConnectionsSection = () => {
  const { showNotification } = useNotifications();
  const [user, setUser] = useState(null);
  const [connections, setConnections] = useState([]);
  const [requests, setRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inviteLink, setInviteLink] = useState("");
  const [isCreatingLink, setIsCreatingLink] = useState(false);
  const inviteLinkInputRef = useRef(null);

  const [tab, setTab] = useState(null);

  const [code, setCode] = useState("");
  const token = localStorage.getItem("token");

  const fetchJson = useCallback(async (path, options = {}) => {
    return apiFetch(path, options);
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      setLoading(true);
      try {
        const userData = await fetchJson("/api/auth/me");
        setUser(userData);
        setTab(userData.accountType === "unternehmen" ? "bedienung" : "unternehmen");
      } catch (err) {
        console.error(err);
        if (!err.sessionExpired) showNotification("error", err.message);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      loadUser();
    }
  }, [fetchJson, showNotification, token]);

  const loadConnections = useCallback(async ({ showLoader = true } = {}) => {
    if (!user) return;

    if (showLoader) {
      setLoading(true);
    }

    try {
      if (tab === "unternehmen") {
        if (user.accountType === "unternehmen") {
          const employees = await fetchJson("/api/connections/employees");
          setConnections(employees);
          setRequests([]);
          setOutgoingRequests([]);
        } else {
          const [companies, incoming] = await Promise.all([
            fetchJson("/api/connections"),
            fetchJson("/api/connections/requests"),
          ]);
          setConnections(companies);
          setRequests(incoming);
          setOutgoingRequests([]);
        }
      } else if (user.accountType === "bedienung") {
        const [peers, incoming, outgoing] = await Promise.all([
          fetchJson("/api/connections/peers"),
          fetchJson("/api/connections/requests/peers/incoming"),
          fetchJson("/api/connections/requests/peers/outgoing"),
        ]);
        setConnections(peers);
        setRequests(incoming);
        setOutgoingRequests(outgoing);
      } else {
        const [employees, outgoing] = await Promise.all([
          fetchJson("/api/connections"),
          fetchJson("/api/connections/requests/outgoing"),
        ]);
        setConnections(employees);
        setRequests([]);
        setOutgoingRequests(outgoing);
      }
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
      throw err;
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  }, [fetchJson, showNotification, tab, user]);

  useEffect(() => {
    loadConnections().catch(() => {});
  }, [loadConnections]);

  const runAction = async (action, successText) => {
    setIsSubmitting(true);

    try {
      await action();
      await loadConnections({ showLoader: false });
      showNotification("success", successText);
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAccept = async (requestId) => {
    await runAction(
      () => fetchJson(`/api/connections/accept/${requestId}`, { method: "POST" }),
      "Anfrage wurde angenommen."
    );
  };

  const handleDelete = async (requestId, type = "outgoing") => {
    let path;
    if (user.accountType === "bedienung") {
      if (tab === "bedienung") {
        path = type === "incoming"
          ? `/api/connections/requests/peers/incoming/${requestId}`
          : `/api/connections/requests/peers/outgoing/${requestId}`;
      } else {
        path = `/api/connections/requests/${requestId}`;
      }
    } else {
      path = `/api/connections/request/${requestId}`;
    }

    await runAction(
      () => fetchJson(path, { method: "DELETE" }),
      type === "incoming" ? "Anfrage wurde abgelehnt." : "Einladung wurde storniert."
    );
  };

  const handleRequest = async (e) => {
    e.preventDefault();
    const isCompany = user.accountType === "unternehmen";
    const endpoint = isCompany
      ? "/api/connections/request/unternehmen"
      : "/api/connections/request";
    const body = isCompany
      ? { code }
      : { code, targetType: tab };

    await runAction(
      async () => {
        await fetchJson(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        setCode("");
      },
      "Einladung wurde gesendet."
    );
  };

  const handleCreateInviteLink = async () => {
    setIsCreatingLink(true);
    try {
      const data = await fetchJson("/api/connections/invite-links", { method: "POST" });
      setInviteLink(data.url);
      showNotification("success", "Einladungslink wurde erstellt.");
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    } finally {
      setIsCreatingLink(false);
    }
  };

  const handleCopyInviteLink = async () => {
    if (!inviteLink) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(inviteLink);
      } else {
        inviteLinkInputRef.current?.select();
        document.execCommand("copy");
      }
      showNotification("success", "Einladungslink wurde kopiert.");
    } catch (err) {
      inviteLinkInputRef.current?.select();
      showNotification("warning", "Der Link wurde markiert und kann manuell kopiert werden.");
    }
  };

  const handleDisconnect = async (item) => {
    let path;
    let confirmText;
    let successText;

    if (user.accountType === "unternehmen" && tab === "bedienung") {
      path = `/api/connections/employees/${item.id}`;
      confirmText = `${item.first_name} ${item.last_name} wirklich aus dem Unternehmen entfernen?`;
      successText = "Bedienung wurde entfernt.";
    } else if (user.accountType === "bedienung" && tab === "unternehmen") {
      path = `/api/connections/companies/${item.id}`;
      confirmText = `${item.firmenname} wirklich verlassen?`;
      successText = "Sie sind aus dem Unternehmen ausgetreten.";
    } else if (user.accountType === "bedienung" && tab === "bedienung") {
      path = `/api/connections/peers/${item.peer_id}`;
      confirmText = `Verbindung zu ${item.first_name} ${item.last_name} wirklich entfernen?`;
      successText = "Verbindung wurde entfernt.";
    } else {
      return;
    }

    if (!window.confirm(confirmText)) return;

    await runAction(
      () => fetchJson(path, { method: "DELETE" }),
      successText
    );
  };

  const emptyText = (text) => <p className="empty-state">{text}</p>;

  const renderPersonTiles = (items, keyName = "id", renderAction = null) => (
    items.length ? (
      <div className="tile-grid connections-grid">
        {items.map(item => (
          <div key={item[keyName] || item.id} className="tile connection-tile">
            <h3>{item.first_name} {item.last_name}</h3>
            {renderAction && (
              <div className="tile-actions">
                {renderAction(item)}
              </div>
            )}
          </div>
        ))}
      </div>
    ) : null
  );

  const renderRequestList = (items, renderName, actionType) => (
    items.length ? (
      <ul className="request-list">
        {items.map(item => (
          <li key={item.id} className="request-item">
            <span>{renderName(item)}</span>
            <div className="request-actions">
              {actionType === "incoming" && (
                <button type="button" onClick={() => handleAccept(item.id)} disabled={isSubmitting}>
                  Annehmen
                </button>
              )}
              <button type="button" onClick={() => handleDelete(item.id, actionType)} disabled={isSubmitting}>
                {actionType === "incoming" ? "Ablehnen" : "Stornieren"}
              </button>
            </div>
          </li>
        ))}
      </ul>
    ) : null
  );

  if (loading) return <p className="connections-loading">Lädt …</p>;

  return (
    <div className="connections-section">
      <div className="tabs">
        <div className={`tab-slider ${tab}`}></div>
        <button className={tab === "bedienung" ? "active" : ""} onClick={() => setTab("bedienung")} disabled={isSubmitting}>
          Bedienungen
        </button>
        <button className={tab === "unternehmen" ? "active" : ""} onClick={() => setTab("unternehmen")} disabled={isSubmitting}>
          Unternehmen
        </button>
      </div>
      {user.accountType === "bedienung" && user.connectionCode && (
        <section className="connection-code-panel" aria-label="Verbindungscode">
          <span>Ihr Verbindungscode</span>
          <strong>{user.connectionCode}</strong>
          <small>Diesen Code benötigt ein Unternehmen, um Sie einzuladen.</small>
        </section>
      )}
      <div className="tab-content">
      {tab === "bedienung" && (
        <>
          <section className="connection-panel invite-link-panel">
            <h2>{user.accountType === "unternehmen" ? "Einladungslink für Bedienungen" : "Verknüpfungslink teilen"}</h2>
            <p className="form-help">
              {user.accountType === "unternehmen"
                ? "Der Link ist zeitlich begrenzt und kann von mehreren Bedienungen genutzt werden. Jede Person muss den Beitritt selbst bestätigen."
                : "Wer diesen Link öffnet, kann sich anmelden oder registrieren und anschließend die Verbindung mit Ihnen bestätigen."}
            </p>
            {!inviteLink ? (
              <button type="button" className="invite-link-create" onClick={handleCreateInviteLink} disabled={isCreatingLink}>
                {isCreatingLink ? "Link wird erstellt …" : "Einladungslink erstellen"}
              </button>
            ) : (
              <div className="invite-link-row">
                <input ref={inviteLinkInputRef} aria-label="Einladungslink" type="text" value={inviteLink} readOnly onFocus={event => event.target.select()} />
                <button type="button" onClick={handleCopyInviteLink}>Link kopieren</button>
                <button type="button" className="secondary-button" onClick={handleCreateInviteLink} disabled={isCreatingLink}>Weiteren Link</button>
              </div>
            )}
          </section>
          {user.accountType === "bedienung" ? (
            <>
              <section className="connection-panel">
                <h2>Ihre Kollegen</h2>
                {renderPersonTiles(connections, "peer_id", peer => (
                  <button type="button" className="danger-button" onClick={() => handleDisconnect(peer)} disabled={isSubmitting}>
                    Verbindung entfernen
                  </button>
                )) || emptyText("Keine verknüpften Kollegen.")}
              </section>

              <section className="connection-panel">
                <h2>Neue Bedienung einladen</h2>
                <p className="form-help">Geben Sie den persönlichen 12-stelligen Verbindungscode der Bedienung ein.</p>
                <form className="connection-form" onSubmit={handleRequest}>
                  <input aria-label="Verbindungscode" type="text" placeholder="ABCD1234EFGH" value={code} onChange={e => setCode(e.target.value.toUpperCase())} minLength="12" maxLength="14" autoCapitalize="characters" autoComplete="off" required disabled={isSubmitting} />
                  <button type="submit" disabled={isSubmitting}>{isSubmitting ? "Sendet …" : "Einladen"}</button>
                </form>
              </section>

              <section className="connection-panel">
                <h2>Offene Einladungen</h2>
                {renderRequestList(requests, r => `${r.first_name} ${r.last_name}`, "incoming") || emptyText("Keine offenen Einladungen.")}
              </section>

              <section className="connection-panel">
                <h2>Gesendete Einladungen</h2>
                {renderRequestList(outgoingRequests, r => `${r.first_name} ${r.last_name}`, "outgoing") || emptyText("Keine gesendeten Einladungen.")}
              </section>
            </>
          ) : (
            <>
              <section className="connection-panel">
                <h2>Ihre Bedienungen</h2>
                {renderPersonTiles(connections, "id", bedienung => (
                  <button type="button" className="danger-button" onClick={() => handleDisconnect(bedienung)} disabled={isSubmitting}>
                    Entfernen
                  </button>
                )) || emptyText("Keine Bedienungen verbunden.")}
              </section>

              <section className="connection-panel">
                <h2>Neue Bedienung einladen</h2>
                <p className="form-help">Die Bedienung findet ihren persönlichen Code in diesem Bereich ihres Accounts.</p>
                <form className="connection-form" onSubmit={handleRequest}>
                  <input aria-label="Verbindungscode" type="text" placeholder="ABCD1234EFGH" value={code} onChange={e => setCode(e.target.value.toUpperCase())} minLength="12" maxLength="14" autoCapitalize="characters" autoComplete="off" required disabled={isSubmitting} />
                  <button type="submit" disabled={isSubmitting}>{isSubmitting ? "Sendet …" : "Einladung senden"}</button>
                </form>
              </section>

              <section className="connection-panel">
                <h2>Gesendete Einladungen</h2>
                {renderRequestList(outgoingRequests, r => `${r.first_name} ${r.last_name}`, "outgoing") || emptyText("Keine offenen Einladungen.")}
              </section>
            </>
          )}
        </>
      )}
      {tab === "unternehmen" && (
        <>
          {user.accountType === "bedienung" ? (
            <>
              <section className="connection-panel">
                <h2>Ihre Unternehmen</h2>
                {connections.length ? (
                  <div className="tile-grid connections-grid">
                    {connections.map(co => (
                      <div key={co.id} className="tile connection-tile">
                        <h3>{co.firmenname}</h3>
                        <div className="tile-actions">
                          <button type="button" className="danger-button" onClick={() => handleDisconnect(co)} disabled={isSubmitting}>
                            Unternehmen verlassen
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : emptyText("Sie sind in keinem Unternehmen registriert.")}
              </section>

              <section className="connection-panel">
                <h2>Offene Firmen-Anfragen</h2>
                {renderRequestList(requests, r => r.firmenname || r.name, "incoming") || emptyText("Keine offenen Anfragen.")}
              </section>
            </>
          ) : user.accountType === "unternehmen" ? (
            <>
              <section className="connection-panel">
                <h2>Firmenaccounts</h2>
                {renderPersonTiles(connections) || emptyText("Keine weiteren Firmenaccounts vorhanden.")}
              </section>
            </>
          ) : null}
        </>
      )}
      </div>
    </div>
  );
};

export default ConnectionsSection;
